import { TrashIcon } from '@heroicons/react/20/solid'

interface PendingDeletionBannerProps {
  /** ISO timestamp when the recording is permanently removed. */
  expiresAt: string | null
  onCancel: () => void
  isCancelling: boolean
}

/** Human "deletes in N days/hours" from now until `expiresAt`. */
function formatCountdown(expiresAt: string | null): string {
  if (!expiresAt) return 'Scheduled for deletion'
  const ms = new Date(expiresAt).getTime() - Date.now()
  if (Number.isNaN(ms) || ms <= 0) return 'Deleting soon'
  const hours = Math.floor(ms / 3_600_000)
  if (hours >= 48) return `Deletes in ${Math.round(hours / 24)} days`
  if (hours >= 24) return 'Deletes in 1 day'
  if (hours >= 1) return `Deletes in ${hours} hour${hours === 1 ? '' : 's'}`
  return 'Deletes within the hour'
}

export function PendingDeletionBanner({
  expiresAt,
  onCancel,
  isCancelling,
}: PendingDeletionBannerProps) {
  return (
    <div
      className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2"
      role="status"
    >
      <span className="flex items-center gap-2 text-xs text-red-200">
        <TrashIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        {formatCountdown(expiresAt)}
      </span>
      <button
        type="button"
        onClick={onCancel}
        disabled={isCancelling}
        className="rounded-md px-3 py-1 text-xs font-semibold text-neon-cyan transition-colors hover:bg-neon-cyan/10 focus:outline-none focus:ring-2 focus:ring-neon-cyan disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isCancelling ? 'Cancelling…' : 'Cancel'}
      </button>
    </div>
  )
}
